import { liteClient } from 'algoliasearch/lite';
import { algoliaConfig } from '../config';
import { formVersionNumberPrefix, formStateDescription, SEARCH_INDEX_SYNC_CONFIG } from '#types/System.Parameters.types';
import { SearchDisplayField } from '#types/System.SearchTypes.types';
import { fieldDataTypes } from '#types';

/**
 * Configuration interface for Algolia search settings
 */
export interface SearchAlgoliaConfig {
  appId: string;
  searchApiKey: string;
  indexName: string;
}

export const searchClient = algoliaConfig?.appId
  ? liteClient(algoliaConfig.appId, algoliaConfig.searchApiKey)
  : null;

/**
 * Standard interface for search result items
 */
export interface SearchResultItem {
  id: string;
  url: string;
  title: string;
  subtitle?: string;
}

/**
 * Safely retrieves a nested value from an object using dot notation
 * @param obj - The object to retrieve from
 * @param path - Dot-separated path (e.g., 'fields.bidName.value')
 * @returns The value at the path or undefined
 */
const getNestedValue = (obj: any, path: string): any => {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

/**
 * Formats a value based on the specified format type
 * @param value - The raw value to format
 * @param format - Optional format type ('currency', 'state', 'versionNumber')
 * @returns Formatted string value
 */
const formatValue = (value: any, format?: string): string => {
  if (value === null || value === undefined || value === '') return '';

  if (format === 'currency') {
    const currencyType = fieldDataTypes.currency;
    if (currencyType.formatter) {
      return `${currencyType.prefix || ''}${currencyType.formatter(value)}`;
    }
  }

  if (format === 'state') {
    return formStateDescription[value as keyof typeof formStateDescription]?.shortLabel || String(value);
  }

  if (format === 'versionNumber') {
    return `${formVersionNumberPrefix}${value}`;
  }

  return String(value);
};

/**
 * Builds a display line from field configurations
 * @param rawItem - The raw search result item
 * @param fields - Array of field configurations
 * @param separator - Separator string between field values
 * @returns Formatted display line string
 */
const buildLine = (rawItem: any, fields: SearchDisplayField[], separator: string): string => {
  return fields
    .map(field => formatValue(getNestedValue(rawItem, field.fieldPath), field.format))
    .filter(Boolean)
    .join(separator);
};

const getResultUrl = (rawItem: any): string => rawItem.searchResultURL || rawItem._documentPath || '';

/**
 * Fallback normalizer for collections without display configuration
 */
const normalizeGenericResult = (rawItem: any): SearchResultItem => ({
  id: rawItem.objectID,
  url: getResultUrl(rawItem),
  title: rawItem.name || rawItem.objectID,
  subtitle: Object.entries(rawItem || {})
    .filter(([key]) => key.startsWith('value_fields_'))
    .slice(0, 5)
    .map(([key, value]) => value && `${key.replace('value_fields_', '')}: ${value}`)
    .filter(Boolean)
    .join(' · ')
});

/**
 * Transforms raw search results into standardized format
 * Uses display configuration from SEARCH_INDEX_SYNC_CONFIG when available
 * @param rawItem - Raw item from search results
 * @returns Standardized search result item
 */
export const normalizeSearchResult = (rawItem: any): SearchResultItem => {
  const documentType = rawItem.documentType || rawItem._collectionName;
  const collectionConfig = SEARCH_INDEX_SYNC_CONFIG.collections[documentType];

  if (collectionConfig?.displayTitleFields) {
    return {
      id: rawItem.objectID,
      url: getResultUrl(rawItem),
      title: buildLine(
        rawItem,
        collectionConfig.displayTitleFields,
        collectionConfig.displayTitleSeparator || ' · '
      ),
      subtitle: buildLine(
        rawItem,
        collectionConfig.displaySubtitleFields || [],
        collectionConfig.displaySubtitleSeparator || ' | '
      )
    };
  }

  return normalizeGenericResult(rawItem);
};
